import { backendApi } from "@/services/backend/api";
import {
  List,
  Process,
  ViewModel,
} from "@/services/backend/processes/types/process";
import { Filter } from "@/shared/lib/filter/filter";
import { Label } from "@/services/backend/processes/api/state";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { ProcessKind, ServiceKind } from "@bitory-gmbh/bsm";
import { UUID } from "@bitory-gmbh/nidavellir";

const processesApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    processesProcessCreate: build.mutation<Process, CreateRequest>({
      query: (request) => ({
        url: `/processes/process`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "Process", id: request.id },
        { type: "Process", id: "LIST" },
      ],
    }),
    processesProcessShow: build.query<ViewModel, ShowRequest>({
      query: (request) => ({
        url: `/processes/process`,
        method: "GET",
        params: request,
      }),
      providesTags: (_result, _e, request) => [
        { type: "Process", id: request.id },
      ],
    }),
    processesProcessList: build.query<List, ListRequest>({
      query: (request) => ({
        url: `/processes/process/list`,
        method: "GET",
        params: { request: btoa(JSON.stringify(request)) },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ id }) => ({
                type: "Process" as const,
                id,
              })),
              { type: "Process", id: "LIST" },
            ]
          : [{ type: "Process", id: "LIST" }],
    }),
    processesProcessListOwningUsers: build.query<
      OwningUserList,
      ListOwningUserRequest
    >({
      query: (request) => ({
        url: `/processes/process/list-owning-users`,
        method: "GET",
        params: { request: btoa(JSON.stringify(request)) },
      }),
      providesTags: [{ type: "Process", id: "LIST" }],
    }),
    processesProcessPatch: build.mutation<Process, PatchRequest>({
      query: (request) => ({
        url: `/processes/process`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "Process", id: request.id },
        { type: "Process", id: "LIST" },
      ],
    }),
    processesProcessDelete: build.mutation<void, DeleteRequest>({
      query: (request) => ({
        url: `/processes/process`,
        method: "DELETE",
        params: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "Process", id: request.id },
        { type: "Process", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useProcessesProcessCreateMutation,
  useProcessesProcessShowQuery,
  useProcessesProcessPatchMutation,
  useProcessesProcessListQuery,
  useProcessesProcessListOwningUsersQuery,
  useProcessesProcessDeleteMutation,
} = processesApi;

export interface CreateRequest {
  id: UUID;
  serviceKind: ServiceKind;
  companyId: UUID;
  branches: UUID[];
  owningUserId: UUID;
  topic?: string;
  customerId?: UUID;
  serviceObjects?: UUID[];
}

export interface ShowRequest {
  id: string;
}

export interface PatchRequest {
  id: UUID;
  customerId?: UUID;
  topic?: string;
  branches?: UUID[];
  serviceObjects?: UUID[];
  owningUserId?: string;
}

export interface DeleteRequest {
  id: string;
}

export interface ListRequest {
  kind?: Filter<ProcessKind>;
  serviceKind?: Filter<ServiceKind>;
  topic?: Filter<string>;
  customer?: Filter<string>;
  serviceObject?: Filter<string>;
  company?: Filter<string>;
  branch?: Filter<string>;
  owningUser?: Filter<string>;
  state?: Filter<Label>;
  createdAt?: Filter<string>;
  updatedAt?: Filter<string>;
  opportunityDataCreatedOn?: Filter<string>;
  opportunityDataModifiedOn?: Filter<string>;
  lastUpdatedDocumentUpdatedAt?: Filter<string>;
  orderBy?: OrdrBy<OrderValues>[];
  limit?: number;
  offset?: number;
}

export interface ListOwningUserRequest {
  company?: Filter<string>;
  branch?: Filter<string>;
  limit?: number;
  offset?: number;
}

export interface OwningUserList {
  items: string[];
  count: number;
  offset: number;
  totalCount: number;
}

export type OrderValues =
  | "processKind"
  | "serviceKind"
  | "topic"
  | "customer"
  | "company"
  | "branch"
  | "owningUser"
  | "state"
  | "createdAt"
  | "updatedAt"
  | "opportunityDataCreatedOn"
  | "opportunityDataModifiedOn"
  | "lastUpdatedDocumentUpdatedAt"
  | "lastActivityAt";
